import { Block, Flex, Icon, theme } from '@aloft/toolkit-sui';

import React, { useState } from 'react';

import { useTkv2ReferenceBarEvents } from '../../components/ReferenceBar/useTkv2ReferenceBarEvents.ts';
import { useLocalStorage } from '../../controller/hooks/useLocalStorage.ts';
import { featuresSelectors } from '../../redux/features/selectors/featuresSelectors.ts';
import { selectHasArvOption } from '../../redux/features/selectors/orderSelectors.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { AnalysisModule } from '../analysis/AnalysisModule.tsx';
import { AppraisifyAnalysisGraphs } from './components/AppraisifyAnalysisGraphs.tsx';
import { AppraisifyAnalysisGrid } from './components/AppraisifyAnalysisGrid.tsx';
import { AppraisifyAnalysisToolbar } from './components/AppraisifyAnalysisToolbar.tsx';
import { VIEW_MODES } from './consts.ts';
import { ViewMode } from './types.ts';

import './AppraisifyAnalysisModule.scss';

export const AppraisifyAnalysisModule = React.memo(() => {
  const [viewMode, setViewMode] = useLocalStorage<ViewMode>({
    key: 'appraisify-analysis:view-mode',
    defaultValue: 'simple',
    opts: {
      serialize: (value) => value,
      deserialize: (value) => (value in VIEW_MODES ? (value as ViewMode) : 'simple'),
    },
  });
  const hasArvOption = useAppSelector(selectHasArvOption);

  const [topHeight, setTopHeight] = useState(Math.max(window.innerHeight / 3, 450));

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const startY = e.clientY;
    const startHeight = topHeight;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const newHeight = startHeight + (moveEvent.clientY - startY);
      if (newHeight > 100 && newHeight < 1000) {
        setTopHeight(newHeight);
      }
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  return (
    <Flex
      {...useTkv2ReferenceBarEvents()}
      className="appraisify-analysis-module"
      width="100%"
      gap={hasArvOption ? 0 : 2}
      paddingLeft="1.5rem"
    >
      <AppraisifyAnalysisToolbar viewMode={viewMode} setViewMode={setViewMode} />
      <Flex flexDirection="column" overflow="hidden" backgroundColor="transparent">
        <Block overflow="auto" height={`${topHeight}px`}>
          <AppraisifyAnalysisGrid viewMode={viewMode} />
        </Block>

        <Block position="relative">
          <Block height="2px" backgroundColor="strokeDefault" />
          <Block
            position="absolute"
            left="50%"
            transform="translate(-50%, -50%)"
            width="fit-content"
            lineHeight="0"
            backgroundColor="surface1"
            padding="0 6px"
            border="2px solid"
            borderColor="strokeDefault"
            borderRadius="8px"
            cursor="row-resize"
            zIndex={theme.zIndices.banner}
            onMouseDown={handleMouseDown}
          >
            <Block margin="-2px 0">
              <Icon icon="resize-handlebar" color="textTertiary" size="lg" />
            </Block>
          </Block>
        </Block>

        <Block flex={1} overflow="auto">
          <AppraisifyAnalysisGraphs />
        </Block>
      </Flex>
    </Flex>
  );
});
